// Ported from dev-scrips-pm-react src/billing/components/claim-review/totals-box.tsx (PROD-3312 appointment Billing tab). PM primitives come from the
// PROD-3310 billing bridge (`@/billing/bridge`); PM's arbitrary-value classes are the named utilities on the same tokens.
import { isSelfPay as isSelfPayType } from '@/billing/lib/sponsor-type';
import type { InvoiceDetail } from '@/shared/types/billing';

import { formatMoney } from '@/shared/lib/money';
/**
 * Right-side totals box.
 *
 * - `variant="claim"` (default) — Flutter `review_claim_popup.dart:1901-2130`
 *   (`procedureDetail()`): the eight claim-review rows. Unchanged.
 * - `variant="invoice"` — Flutter `invoice_billing_screen.dart` amount-summary
 *   (appointment / patient billing tab): Gross / Discount / Net / Patient
 *   Payable (emphasis) / Deposited / Outstanding, with Patient Share / Excluded
 *   / Sponsor-* shown only for non-Self-pay, and Management Discount only on
 *   Self-pay && > 0.
 *
 * Values are server-computed; the React side never recalculates locally.
 */
interface Props {
  invoice: InvoiceDetail;
  /** ISO-4217 code, e.g. "AED". Falls back to the literal "AED" per Flutter. */
  currency?: string;
  variant?: 'claim' | 'invoice';
  /**
   * Invoice variant only: when provided, the Patient Payable row becomes a
   * button that opens the management-discount popup (Flutter: tap Patient
   * Payable on a Self-pay, not-yet-issued invoice). The caller decides
   * eligibility and omits this otherwise.
   */
  onPatientPayableClick?: () => void;
}

export function ClaimReviewTotalsBox({
  invoice,
  currency = 'AED',
  variant = 'claim',
  onPatientPayableClick,
}: Props) {
  if (variant === 'invoice') {
    // Server field drives sponsor gating; normalised exact match, fail-open
    // (absent field => not Self-pay => show all rows; billing transparency).
    const isSelfPay = isSelfPayType(invoice.sponsorType);
    // Once a management discount exists, the blue/clickable affordance for the
    // discount dialog moves ONTO the Management Discount row and Patient Payable
    // demotes to plain emphasised text (PROD-1793). Two separate switches:
    //   colour (blue)  ← managementDiscount > 0
    //   clickable      ← additionally the Draft-gated `onPatientPayableClick`
    // so an Issued invoice shows the row blue-but-inert (CP-M4) and this stays
    // separate from the PROD-1759 locked-record gating.
    const hasMgmtDiscount = isSelfPay && (invoice.managementDiscount ?? 0) > 0;
    const discountRowClickable = hasMgmtDiscount && Boolean(onPatientPayableClick);
    return (
      <section data-billing="totals" className="rounded-xl border border-border-default bg-surface-card px-6 py-6">
        <dl className="flex flex-col gap-2 text-sm leading-5">
          <Row label="Gross Total" value={invoice.grossTotal} currency={currency} />
          <Row label="Discount Total" value={invoice.discountTotal} currency={currency} />
          <Row label="Net Total" value={invoice.netTotal} currency={currency} />
          {!isSelfPay && (
            <Row label="Patient Share Total" value={invoice.patientShareTotal} currency={currency} />
          )}
          {!isSelfPay && (
            <Row label="Excluded Total" value={invoice.excludedTotal} currency={currency} />
          )}
          {!isSelfPay && (
            <Row label="Sponsor Amount Total" value={invoice.sponsorAmountTotal} currency={currency} />
          )}
          {/* Management Discount is the deduction that PRODUCES Patient Payable,
              so it renders immediately BEFORE Patient Payable, not below
              Outstanding (PROD-1758). When a discount exists it also carries the
              blue/clickable affordance for the discount dialog (PROD-1793). */}
          {hasMgmtDiscount &&
            (discountRowClickable ? (
              <button
                type="button"
                onClick={onPatientPayableClick}
                title="Edit management discount"
                className="flex items-baseline justify-between gap-6 text-left w-full"
              >
                <dt className="text-xs font-medium leading-4 text-brand-primary">Management Discount</dt>
                <dd className="tabular-nums text-brand-primary">
                  {currency} {fmtAmount(invoice.managementDiscount)}
                </dd>
              </button>
            ) : (
              <div className="flex items-baseline justify-between gap-6">
                <dt className="text-xs font-medium leading-4 text-brand-primary">Management Discount</dt>
                <dd className="tabular-nums text-brand-primary">
                  {currency} {fmtAmount(invoice.managementDiscount)}
                </dd>
              </div>
            ))}
          {/* Patient Payable is the discount-dialog trigger ONLY when no discount
              exists yet; once one is applied the trigger moves to the row above
              and this becomes plain emphasised text (PROD-1793). */}
          {!hasMgmtDiscount && onPatientPayableClick ? (
            <button
              type="button"
              onClick={onPatientPayableClick}
              title="Add management discount"
              className="flex items-baseline justify-between gap-6 text-left w-full"
            >
              <dt className="font-semibold text-brand-primary">Patient Payable</dt>
              <dd className="tabular-nums font-semibold text-brand-primary">
                {currency} {fmtAmount(invoice.patientPayable)}
              </dd>
            </button>
          ) : (
            <Row
              label="Patient Payable"
              value={invoice.patientPayable}
              currency={currency}
              emphasis
            />
          )}
          <Row label="Deposited" value={invoice.deposited} currency={currency} />
          <Row label="Outstanding" value={invoice.patientOutstanding} currency={currency} />
          {!isSelfPay && (
            <Row label="Sponsor Outstanding" value={invoice.sponsorOutstanding} currency={currency} />
          )}
        </dl>
      </section>
    );
  }

  return (
    <section data-billing="totals" className="rounded-xl border border-border-default bg-surface-card px-6 py-6">
      <dl className="flex flex-col gap-2 text-sm leading-5">
        <Row
          label="Net Total"
          value={invoice.netTotal}
          currency={currency}
          emphasis
        />
        <Row
          label="Patient Share Total"
          value={invoice.patientShareTotal}
          currency={currency}
        />
        <Row
          label="Excluded Total"
          value={invoice.excludedTotal}
          currency={currency}
        />
        <Row
          label="Patient Payable"
          value={invoice.patientPayable}
          currency={currency}
        />
        <Row
          label="Patient Outstanding"
          value={invoice.patientOutstanding}
          currency={currency}
        />
        <Row
          label="Sponsor Amount Total"
          value={invoice.sponsorAmountTotal}
          currency={currency}
        />
        <Row
          label="Sponsor Payment Total"
          value={invoice.sponsorPaymentTotal}
          currency={currency}
        />
        <Row
          label="Sponsor Outstanding Total"
          value={invoice.sponsorOutstanding}
          currency={currency}
        />
      </dl>
    </section>
  );
}

function Row({
  label,
  value,
  currency,
  emphasis = false,
}: {
  label: string;
  value: number | null | undefined;
  currency: string;
  emphasis?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-6">
      <dt
        className={
          emphasis
            ? 'font-semibold text-text-primary'
            : 'text-xs font-medium leading-4 text-text-secondary'
        }
      >
        {label}
      </dt>
      <dd
        className={`tabular-nums text-text-primary ${
          emphasis ? 'font-semibold' : ''
        }`}
      >
        {currency} {fmtAmount(value)}
      </dd>
    </div>
  );
}

function fmtAmount(value: number | null | undefined): string {
  if (value == null) return '0.00';
  return formatMoney(value);
}
